import React, { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SkeletonLoader, { SkeletonCircle } from "@/components/ui/SkeletonLoader";

const DonutChart = ({
  data,
  title,
  keyColumn,
  valueColumn,
  pinColumn = null,
  isLoaded = false,
  error = null,
  height = 225,
  maxLegendHeight = 200,
  truncateSize = 6,
  truncateLegend = true,
  noDataMessage,
  minHeight = 225,
  colors,
  className = "",
}) => {
  const { t } = useTranslation();
  const resolvedNoDataMessage =
    noDataMessage ?? t("common.noDataGeneric", "No data available");
  const canvasRef = useRef(null);

  // Resolve theme palette from CSS custom properties
  const themeColors = useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const get = (v) => s.getPropertyValue(v).trim();
    return [
      get("--accent-lime"),
      get("--brand-color-dark"),
      get("--brand-color-1"),
      get("--brand-color-2"),
      get("--success"),
      get("--warning"),
      get("--info-light"),
      get("--funnel-top-bg"),
    ];
  }, []);

  const resolvedColors = colors ?? themeColors;

  // Sort data by value in descending order
  const sortedDataWithOutPin = React.useMemo(() => {
    return [...(data ?? [])].sort((a, b) => b[valueColumn] - a[valueColumn]);
  }, [data, valueColumn]);

  // If pinColumn is provided, sort by it first, then by value
  const sortedData = React.useMemo(() => {
    if (!pinColumn) return sortedDataWithOutPin;

    return [...sortedDataWithOutPin].sort((a, b) => {
      // If both items have the same pin status, sort by value
      if (a[pinColumn] === b[pinColumn]) {
        return b[valueColumn] - a[valueColumn]; // Sort by value if pin is the same
      }
      // Pin true comes first
      return a[pinColumn] ? -1 : 1;
    });
  }, [sortedDataWithOutPin, pinColumn, valueColumn]);

  const otherLabel = t("common.other", "Other");

  const truncatedData = React.useMemo(() => {
    // only keep the first 6 entries and group the rest into "Other"
    if (sortedData.length <= truncateSize) return sortedData;
    const topItems = sortedData.slice(0, truncateSize);
    const otherTotal = sortedData
      .slice(truncateSize)
      .reduce((sum, item) => sum + item[valueColumn], 0);
    return [...topItems, { [keyColumn]: otherLabel, [valueColumn]: otherTotal }];
  }, [sortedData, keyColumn, valueColumn, truncateSize, otherLabel]);

  // Legend data - controlled by truncateLegend prop
  const legendData = React.useMemo(() => {
    const dataToUse = truncateLegend ? truncatedData : sortedData;
    const total = sortedData.reduce((sum, item) => sum + item[valueColumn], 0);

    return dataToUse.map((item, index) => ({
      label: item[keyColumn],
      value: item[valueColumn],
      color: truncateLegend
        ? resolvedColors[index % resolvedColors.length]
        : index < truncateSize
          ? resolvedColors[index % resolvedColors.length]
          : resolvedColors[truncateSize],
      percentage:
        total > 0 ? ((item[valueColumn] / total) * 100).toFixed(1) : "0.0",
    }));
  }, [
    sortedData,
    truncatedData,
    keyColumn,
    valueColumn,
    resolvedColors,
    truncateLegend,
    truncateSize,
  ]);

  // Generate colors for data items (memoized to avoid identity churn)
  // Note From Mahdee: The chart data contains only truncated items
  const chartData = React.useMemo(
    () =>
      truncatedData.map((item, index) => ({
        label: item[keyColumn],
        value: item[valueColumn],
        color: resolvedColors[index % resolvedColors.length],
      })),
    [truncatedData, keyColumn, valueColumn, resolvedColors],
  );

  // Determine if a fixed numeric height was provided or a flexible/string height
  const isFixedHeight = typeof height === "number";
  const fallbackHeight = Number(minHeight) || 225;
  const effectiveMinHeight = isFixedHeight ? Number(height) : fallbackHeight;

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    let rafId = null;

    const drawChart = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      // Compute desired canvas size (CSS pixels)
      const pw = parent.clientWidth;
      const ph = parent.clientHeight;
      let canvasSizePx = 0;

      if (isFixedHeight) {
        canvasSizePx = Math.max(0, Math.floor(Number(height)));
      } else {
        canvasSizePx = Math.max(0, Math.floor(Math.min(pw, ph)));
      }

      if (canvasSizePx <= 0) return;

      const dpr = window.devicePixelRatio || 1;
      const desiredInternalFloat = canvasSizePx * dpr;
      const desiredInternal = Math.round(desiredInternalFloat);
      const desiredCss = `${canvasSizePx}px`;

      // Avoid toggling the canvas size/style if it already matches
      const shouldResize =
        canvas.width !== desiredInternal ||
        canvas.height !== desiredInternal ||
        canvas.style.width !== desiredCss ||
        canvas.style.height !== desiredCss;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (shouldResize) {
        canvas.width = desiredInternal;
        canvas.height = desiredInternal;
        canvas.style.width = desiredCss;
        canvas.style.height = desiredCss;
      }

      // Reset transform and scale for DPR
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvasSizePx, canvasSizePx);

      // Resolve theme colors for canvas drawing
      const style = getComputedStyle(document.documentElement);
      const colorBgElevated =
        style.getPropertyValue("--bg-elevated").trim() || "#ffffff";
      const colorBorder =
        style.getPropertyValue("--border").trim() || "#e2e8f0";

      // Return early if no data or not loaded
      if (!chartData.length || !isLoaded) return;

      const size = Math.min(canvas.width / dpr, canvas.height / dpr);
      const centerX = size / 2;
      const centerY = size / 2;
      const radius = size * 0.4;

      // Calculate total value
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      if (total === 0) return;

      // Draw donut chart
      let startAngle = -Math.PI / 2; // Start from top
      chartData.forEach((item) => {
        const sliceAngle = (2 * Math.PI * item.value) / total;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
        ctx.closePath();

        ctx.fillStyle = item.color;
        ctx.fill();

        // Add subtle border
        ctx.strokeStyle = colorBgElevated;
        ctx.lineWidth = 2;
        ctx.stroke();

        startAngle += sliceAngle;
      });

      // Draw a white circle in the middle for donut effect
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.6, 0, 2 * Math.PI);
      ctx.fillStyle = colorBgElevated;
      ctx.fill();
      ctx.strokeStyle = colorBorder;
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    // Initial draw
    drawChart();

    // Schedule redraw with rAF to avoid ResizeObserver sync loops
    const scheduleDraw = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(drawChart);
    };

    let ro;
    if (!isFixedHeight && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => scheduleDraw());
      if (canvas.parentElement) ro.observe(canvas.parentElement);
    }

    window.addEventListener("resize", scheduleDraw);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", scheduleDraw);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [chartData, height, isLoaded, isFixedHeight]);

  return (
    <div
      className={`bg-white border card-radius position-relative d-flex flex-column${className ? ` ${className}` : ""}`}
      style={{
        borderColor: "var(--border)",
        width: "100%",
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {title && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="text-sm fw-medium text-dark">{title}</div>
        </div>
      )}

      {/* Centered No Data Overlay moved into content area to avoid overlapping title */}

      <div className="p-3" style={{ position: "relative", flex: 1 }}>
        {/* Overlay inside content area so it doesn't cover the title */}
        {!error && isLoaded && chartData.length === 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 5,
              pointerEvents: "none",
            }}
          >
            <div
              className="d-flex align-items-center text-muted"
              style={{ gap: "8px" }}
            >
              <i
                className="bi bi-pie-chart"
                style={{ fontSize: "1.25rem" }}
              ></i>
              <div>{resolvedNoDataMessage}</div>
            </div>
          </div>
        )}
        <div className="row align-items-center">
          {/* Error State */}
          {error && (
            <div
              className="col-12 d-flex justify-content-center align-items-start"
              style={{ minHeight: `${effectiveMinHeight}px` }}
            >
              <div className="text-center align-self-start mt-2 mx-3">
                <div className="alert alert-danger mb-0" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {t(
                    "common.loadError",
                    "An error occurred while loading the data. Please try again later.",
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {!error && !isLoaded && (
            <>
              <div className="col-12 col-xl-5 d-flex justify-content-center mb-3 mb-xl-0 p-2">
                <div
                  className="position-relative w-100"
                  style={{
                    height: isFixedHeight ? `${height}px` : undefined,
                    width: isFixedHeight ? `${height}px` : "100%",
                    minHeight: `${effectiveMinHeight}px`,
                    maxWidth: "100%",
                    aspectRatio: "1 / 1",
                  }}
                >
                  <SkeletonCircle
                    size="100%"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                </div>
              </div>

              <div className="col-12 col-xl-7 d-flex justify-content-center justify-content-xl-start">
                <div
                  className="d-flex flex-column gap-2"
                  style={{
                    width: "100%",
                    ...(truncateLegend
                      ? {}
                      : {
                          maxHeight: `${maxLegendHeight}px`,
                          paddingRight: "8px",
                        }),
                  }}
                >
                  {["68%", "82%", "74%", "79%", "63%", "70%"].map(
                    (w, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <SkeletonLoader
                          width="12px"
                          height="12px"
                          borderRadius="4px"
                          className="me-2"
                        />
                        <SkeletonLoader width={w} height="14px" />
                      </div>
                    ),
                  )}
                </div>
              </div>
            </>
          )}

          {/* Loaded State (chart container always present when loaded) */}
          {!error && isLoaded && (
            <>
              {/* Chart container - always rendered to preserve height */}
              <div className="col-12 col-xl-5 d-flex justify-content-center mb-3 mb-xl-0 p-2">
                <div
                  className="position-relative w-100"
                  style={{
                    height: isFixedHeight ? `${height}px` : undefined,
                    width: isFixedHeight ? `${height}px` : "100%",
                    minHeight: `${effectiveMinHeight}px`,
                    maxWidth: "100%",
                    aspectRatio: "1 / 1",
                  }}
                >
                  {chartData.length > 0 && (
                    <canvas
                      ref={canvasRef}
                      {...(isFixedHeight
                        ? { width: height, height: height }
                        : {})}
                      className="w-100 h-100"
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Legend with conditional scroll - only when there's data */}
              {chartData.length > 0 && (
                <div className="col-12 col-xl-7 d-flex justify-content-center justify-content-xl-start">
                  <div
                    className={`d-flex flex-column gap-2 ${!truncateLegend ? "overflow-auto" : ""}`}
                    style={{
                      ...(truncateLegend
                        ? {}
                        : {
                            maxHeight: `${maxLegendHeight}px`,
                            paddingRight: "8px", // Add some padding for scrollbar
                          }),
                    }}
                  >
                    {legendData.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center flex-shrink-0"
                      >
                        <div
                          className="rounded me-2 flex-shrink-0"
                          style={{
                            backgroundColor: item.color,
                            width: "12px",
                            height: "12px",
                          }}
                        ></div>
                        <span className="text-sm text-dark">
                          <span className="fw-medium text-truncate">
                            {item.label}:
                          </span>{" "}
                          {item.value} ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* No Data State (handled by overlay) */}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
